import { useEffect, useState } from 'react';

const EXIT_MS = 180;

/**
 * Drives a modal's enter/exit lifecycle from a nullable source value.
 *
 * Returns the value persisted through the exit animation (`data`), whether the
 * node should be mounted (`render`), and whether the open-state classes apply
 * (`visible`). State changes are deferred into requestAnimationFrame/timeout
 * callbacks so the dialog mounts at scale-95 and then transitions to scale-100.
 */
export function useModalState<T>(value: T | null) {
  const [data, setData] = useState<T | null>(value);
  const [render, setRender] = useState<boolean>(Boolean(value));
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (value) {
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        setData(value);
        setRender(true);
        raf2 = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
    let timer = 0;
    const raf = requestAnimationFrame(() => {
      setVisible(false);
      timer = window.setTimeout(() => setRender(false), EXIT_MS);
    });
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [value]);

  return { data, render, visible };
}
